import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function TerminationForCause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" data-testid="home-link" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <div className="h-32 md:h-48" style={{ background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.background} 50%, ${colors.backgroundLight} 100%)` }} />

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 data-testid="page-title" className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            What Does a Termination for Cause Clause Mean?
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                A termination for cause clause allows an employer to end your employment immediately if you do something serious that violates the terms of your agreement. "Cause" typically means significant misconduct or failure to perform—not minor mistakes or personality conflicts.
              </p>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                When termination happens "for cause," it usually means you won't receive severance pay, notice periods may not apply, and you might lose certain benefits. The clause defines what counts as serious enough to justify immediate termination.
              </p>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Example Clause</h2>
              <div className="p-6 rounded-xl text-base" style={{ backgroundColor: colors.backgroundDark, color: colors.text, fontFamily: 'monospace' }}>
                "The Company may terminate this agreement immediately for Cause. 'Cause' shall include but not be limited to: (a) willful misconduct or gross negligence in the performance of duties; (b) conviction of a felony or crime involving moral turpitude; (c) material breach of company policies; or (d) engaging in conduct materially injurious to the Company's reputation or business interests."
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Why This Clause Is Used</h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                Employers include termination for cause clauses to protect themselves from serious problems:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To end employment quickly when someone's actions create risk
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To avoid paying severance when termination results from the employee's serious misconduct
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To clearly define what behaviors are unacceptable
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To create legal grounds for termination without typical notice requirements
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>What This Usually Affects</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Notice requirements:</strong> Often waived when someone is terminated for cause
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Severance pay:</strong> Typically not provided in for-cause terminations
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Vested benefits:</strong> May be forfeited depending on the agreement
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>References:</strong> Can affect what future employers learn about your departure
                </li>
              </ul>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Employment law varies significantly by location. What constitutes "cause" can be subject to interpretation and dispute. Even with a cause clause, employers may still need to follow certain procedures or provide documentation. This explanation covers typical contract language but doesn't constitute legal advice about your specific situation or rights in a termination scenario.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/explain-notice-period-clause" data-testid="related-link-notice" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Notice Period</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Learn about advance warning requirements</p>
                </Link>
                <Link to="/explain-fixed-term-employment-clause" data-testid="related-link-fixed-term" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Fixed Term Employment</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Understand time-limited employment agreements</p>
                </Link>
              </div>
            </section>

            <div data-testid="cta-section" className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
              <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
              <Link to="/" data-testid="cta-button" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
                Try ClauseClear
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Sponsorship Placeholder */}
            <SponsorPlaceholder variant="full" />
          </div>
        </div>
      </article>
    </div>
  );
}
